import React, { useMemo } from 'react';
import {
  EChart,
  truncateLabel,
  getLabelWidth,
  getTruncateLength,
  getCompactGrid,
  getFocusCartesianGrid,
  getPreviewGrid,
} from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { EChartsOption } from 'echarts';

export interface MilestoneVarianceChartProps {
  data: Array<{ name: string; varianceDays: number | null }>;
  height: number | string;
  onBarClick: () => void;
  showDataLabels?: boolean;
  density?: ChartDensity;
}

export function MilestoneVarianceChart({
  data,
  height,
  onBarClick,
  showDataLabels = true,
  density = 'overview',
}: MilestoneVarianceChartProps) {
  const labelWidth = getLabelWidth(density);
  const truncLen = getTruncateLength(density);
  const compactGrid = getCompactGrid(false);
  const previewGrid = getPreviewGrid(false);

  const option = useMemo<EChartsOption>(() => ({
    tooltip:
      density === 'preview'
        ? { show: false }
        : {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: unknown) => {
              const arr = Array.isArray(params) ? params : [params];
              const item = arr[0] as { dataIndex?: number; value?: number } | undefined;
              if (!item) return '';
              const row = data[item.dataIndex ?? 0];
              const fullName = row?.name ?? '';
              const v = item.value ?? 0;
              const sign = v > 0 ? '+' : '';
              return `<div style="font-weight:600;max-width:280px;white-space:normal;word-break:break-word;">${fullName}</div><div>Variance: ${sign}${v}d</div>`;
            },
          },
    grid:
      density === 'preview' ? previewGrid : density === 'overview' ? compactGrid : getFocusCartesianGrid(),
    xAxis: {
      type: 'value',
      name: 'Variance (days)',
      nameLocation: 'middle',
      nameGap: 24,
      axisLabel: {
        formatter: '{value}d',
        fontSize: density === 'preview' ? 9 : 10,
      },
    },
    yAxis: {
      type: 'category',
      data: data.map((d) => truncateLabel(d.name ?? '—', truncLen)),
      inverse: true,
      axisLabel: {
        fontSize: density === 'preview' ? 9 : 10,
        width: labelWidth,
        overflow: 'truncate',
        ellipsis: '…',
      },
    },
    series: [{
      type: 'bar',
      data: data.map((d) => d.varianceDays ?? 0),
      label: {
        show: density === 'preview' ? false : showDataLabels && data.length <= 15,
        fontSize: 9,
        position: 'right',
        distance: 6,
        formatter: (params: { value?: unknown }) => {
          const v = params.value;
          if (typeof v !== 'number') return '';
          return v > 0 ? `+${v}d` : `${v}d`;
        },
      },
      labelLayout: { hideOverlap: true },
      barMaxWidth: 16,
      itemStyle: {
        color: (params: { data?: unknown }) => {
          const v = typeof params.data === 'number' ? params.data : 0;
          return v > 0 ? '#EF4444' : '#22C55E';
        },
      },
    }],
  }), [data, showDataLabels, labelWidth, truncLen, density, compactGrid, previewGrid]);

  const onEvents = useMemo(() => ({ click: onBarClick }), [onBarClick]);
  return <EChart option={option} height={height} onEvents={onEvents} />;
}
